// @flow
import React, { useState, useEffect, useContext, useCallback } from 'react';
import SortableTree, { getFlatDataFromTree } from 'react-sortable-tree';
import { uniqBy, xor, isEmpty, noop } from 'lodash';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import Tick from '@atlaskit/icon/glyph/check-circle';
import LoadingOverlay from 'react-loading-overlay';
import { v4 as uuidV4 } from 'uuid';
import Flag, { FlagGroup } from '@atlaskit/flag';
import Spinner from '@atlaskit/spinner';
import { token, useThemeObserver } from '@atlaskit/tokens';
import FileExplorerTheme from './Theme';
import { COMPONENT_HIERARCHY_TYPES, OCTO_GLOBAL_PERMISSIONS } from '../../utils/consts';
import Loader from '../utils/Loader/Loader';
import {
    VIRTUAL_COMPONENTS_HIERARCHY_QUERY_PAGINATED,
    VIRTUAL_COMPONENTS_HIERARCHY_CHILDREN,
    VIRTUAL_LABELS_CHILDREN,
} from '../../graphql/queries';
import {
    componentsLabelsHierarchyTreeFlatData,
    getAllComponentChildrenFrom,
    hasSameComponentRootSubTree,
    getComponentParentLabel,
    getParentLabelsPath,
    arrayToTree,
    isItemSelectedDescendant,
} from './utils';
import { GridWrapper, LoadingContainer, TableHeader } from './utils/styles';
import EmptyListView from './components/empty';
import EmptyFilterView from './components/empty-search';
import OctoPermissionsContext from '../../providers/OctoPermissionsContext';
import { hasPermission } from '../../utils/commons';
import PaginationContainer from '../pagination/PaginationContainer';

LoadingOverlay.propTypes = undefined;

export const RED = '#DE350B';
export const GREEN = '#00875A';

const virtualComponentInputSample = {
    id: '',
    field: '',
    parentId: '',
    name: '',
    description: '',
    assigneeType: '',
    projects: [],
    virtualLabels: [],
    projectsToCreate: [],
    projectsToRemove: [],
};

type VirtualComponentsHierarchyTableProps = {
    updateMultipleVirtualComponents: any,
    filters: any,
    client: any,
    shouldUpdateTable: any,
    editVirtualComponent: any,
    setToDeleteLabel: any,
    pagination: any,
    onPageChange: any,
    shouldGroupByLabels: boolean,
};

const VirtualComponentsHierarchyTable = ({
    updateMultipleVirtualComponents,
    filters,
    client,
    shouldUpdateTable,
    editVirtualComponent,
    setToDeleteLabel,
    pagination,
    onPageChange,
    shouldGroupByLabels,
}: VirtualComponentsHierarchyTableProps) => {
    const { colorMode } = useThemeObserver();
    /* State - Start */
    const [virtualLabels, setLabels] = useState([]);
    const [virtualComponents, setVirtualComponents] = useState([]);
    const [treeData, setTreeData] = useState([]);
    const [flatTreeData, setFlatTreeData] = useState([]);
    const [treeDataExpandedNodes, setTreeDataExpandedNodes] = useState({});
    const [treeChildrenDataCalled, setTreeChildrenDataCalled] = useState({});
    const [flags, setFlags] = useState([]);
    const [shouldUpdate, setShouldUpdate] = useState(shouldUpdateTable);
    const [isLoading, setIsLoading] = useState(false);
    const [isFirstLoading, setIsFirstLoading] = useState(true);

    const [, setTreeFilters] = useState();
    const permissions = useContext(OctoPermissionsContext);

    const [count, setCount] = useState(0);

    /* State - End */

    /* Help Functions - Start */
    const hasFilters = () => {
        const { search, projects, labels } = filters;
        return !(isEmpty(search) && isEmpty(projects) && isEmpty(labels));
    };

    const hasPermissionToEdit = () => hasPermission(OCTO_GLOBAL_PERMISSIONS.MANAGE, permissions);
    /* Help Functions - End */

    /* React Effects - Start */
    useEffect(() => {
        if (flags.length) {
            const filteredFlags = flags.filter((flag) => {
                return flag.autoDismiss !== undefined && flag.autoDismiss === false;
            });
            if (filteredFlags.length !== flags.length) {
                setTimeout(() => setFlags(filteredFlags), 5000);
            }
        }
    }, [flags]);

    useEffect(() => {
        setTreeFilters(filters);
    }, [filters]);

    useEffect(() => {
        setTreeDataExpandedNodes([]);
    }, [filters.search]);

    const updateData = useCallback(async () => {
        if (shouldUpdateTable || shouldUpdate) {
            setIsLoading(true);
            setTimeout(async () => {
                const labels = (filters.labels || []).map(({ value }) => value);
                const projects = (filters.projects || []).map(({ value }) => value);
                const variables = {
                    filter: {
                        virtualComponentName: filters.search,
                        labels,
                        projects,
                        shouldGroupByLabels,
                    },
                    pagination,
                };
                const [components] = await Promise.all([
                    await client.query({
                        query: VIRTUAL_COMPONENTS_HIERARCHY_QUERY_PAGINATED,
                        variables,
                        fetchPolicy: 'network-only',
                    }),
                ]);

                const vcs = (components.data?.virtualComponentsHierarchyPaginated?.components || []).map((vc) => {
                    const treeId = uuidV4();
                    return {
                        ...vc,
                        uniqueId: uuidV4(),
                        treeId,
                        path: [treeId],
                    };
                });
                setVirtualComponents(vcs);
                const lbls = (components.data?.virtualComponentsHierarchyPaginated?.labels || []).map((label) => {
                    const labelTreeId = uuidV4();
                    return {
                        ...label,
                        treeId: labelTreeId,
                        path: [labelTreeId],
                    };
                });
                setLabels(lbls);
                setCount(components.data?.virtualComponentsHierarchyPaginated?.count);

                setIsLoading(false);
                setShouldUpdate(false);
            }, 300);
        }
    }, [
        shouldUpdateTable,
        shouldUpdate,
        filters.labels,
        filters.projects,
        filters.search,
        pagination,
        client,
        shouldGroupByLabels,
    ]);

    useEffect(() => {
        updateData();
    }, [updateData]);

    const getTreeItemParentsPathIds = (treeItem, treeDataFlatDataArray) => {
        const parentsIds = [];
        let currentParentId = treeItem.parentId;
        while (!isEmpty(currentParentId)) {
            const currentParent = treeDataFlatDataArray.find(
                (cp) => cp.id === currentParentId || cp.treeId === currentParentId,
            );
            parentsIds.push(currentParent.treeId);
            if (currentParent && currentParent.parentId) {
                currentParentId = currentParent.parentId;
            } else {
                currentParentId = null;
            }
        }
        return parentsIds;
    };

    // Effect to deal with filtering
    useEffect(() => {
        let tree;
        const treeItemFilteredParentIds = [];
        let treeItemFilteredParents = [];
        const { projects, labels } = filters;

        if (isEmpty(projects) && isEmpty(labels)) {
            const flatTree = componentsLabelsHierarchyTreeFlatData(
                virtualLabels,
                virtualComponents,
                treeDataExpandedNodes,
            );
            setFlatTreeData(flatTree);
            tree = arrayToTree(flatTree);
            setTreeData(tree);
            return;
        }

        const treeDataFlatDataArray = componentsLabelsHierarchyTreeFlatData(
            virtualLabels,
            virtualComponents,
            treeDataExpandedNodes,
        );

        let treeDataFlatDataArrayFiltered = treeDataFlatDataArray.filter((treeItem) => {
            let shouldReturn = true;
            if (
                shouldReturn &&
                !isEmpty(labels) &&
                treeItem.type === COMPONENT_HIERARCHY_TYPES.LABEL &&
                !labels.find((lblFilter) => lblFilter.value === treeItem.id)
            ) {
                shouldReturn = false;
            }
            if (shouldReturn && treeItem.parentId) {
                const parents = getTreeItemParentsPathIds(treeItem, treeDataFlatDataArray);
                treeItemFilteredParentIds.push(...parents);
            }
            return shouldReturn;
        });

        treeItemFilteredParents = treeDataFlatDataArray.filter((treeItem) =>
            treeItemFilteredParentIds.includes(treeItem.treeId),
        );
        treeDataFlatDataArrayFiltered = [...treeDataFlatDataArrayFiltered, ...treeItemFilteredParents];
        treeDataFlatDataArrayFiltered = uniqBy(treeDataFlatDataArrayFiltered, (item) => item.treeId);

        setFlatTreeData(treeDataFlatDataArrayFiltered);
        tree = arrayToTree(treeDataFlatDataArrayFiltered);
        setTreeData(tree);
    }, [filters, virtualComponents, virtualLabels, treeDataExpandedNodes]);

    /* React Effects - End */

    const showSuccessMessage = useCallback((msg) => {
        setFlags([
            {
                icon: <Tick primaryColor={GREEN} />,
                title: msg || 'Label updated successfully.',
                key: 'success',
            },
        ]);
    }, []);

    const showErrorMessage = useCallback((msg) => {
        setFlags([
            {
                icon: <WarningIcon primaryColor={RED} />,
                title: `Warning`,
                description: msg || `It was not possible to update selected item. Please, try again later.`,
                key: 'Warning',
                autoDismiss: true,
                actions: [
                    {
                        content: 'Dismiss',
                        onClick: () => {
                            setFlags([]);
                        },
                    },
                ],
            },
        ]);
    }, []);

    /* Table related methods */
    const updateMultipleComponents = useCallback(
        (input) => {
            const labels = (filters.labels || []).map(({ value }) => value);
            const projects = (filters.projects || []).map(({ value }) => value);
            const variables = {
                filter: {
                    virtualComponentName: filters.search,
                    labels,
                    projects,
                    shouldGroupByLabels,
                },
                pagination,
            };
            setIsLoading(true);
            updateMultipleVirtualComponents({
                variables: { input },
                refetchQueries: [{ query: VIRTUAL_COMPONENTS_HIERARCHY_QUERY_PAGINATED, variables }],
            })
                .then(() => {
                    setShouldUpdate(true);
                    setIsLoading(false);
                    showSuccessMessage('Component updated successfully.');
                })
                .catch((e) => {
                    console.log(e);
                    showErrorMessage();
                    setIsLoading(false);
                });
        },
        [
            filters.labels,
            filters.projects,
            pagination,
            filters.search,
            shouldGroupByLabels,
            showErrorMessage,
            showSuccessMessage,
            updateMultipleVirtualComponents,
        ],
    );

    const handleDragLabelDropIntoComponent = useCallback(() => {
        setFlags([
            {
                icon: <WarningIcon primaryColor={RED} />,
                title: `Action not allowed`,
                description: `It's not possible to drop a label into a component.`,
                key: 'Warning',
                autoDismiss: true,
                actions: [
                    {
                        content: 'Dismiss',
                        onClick: () => {
                            setFlags([]);
                        },
                    },
                ],
            },
        ]);
    }, []);

    const getChangeComponentParentIdInput2 = useCallback((node, nextParentNode) => {
        let changeInput;

        if (nextParentNode) {
            if (nextParentNode.type === COMPONENT_HIERARCHY_TYPES.LABEL) {
                if (node.parentId) {
                    changeInput = {
                        ...virtualComponentInputSample,
                        id: node.id,
                        field: 'remove-parent',
                        parentId: node.parentId,
                    };
                }
            }
            if (nextParentNode.type === COMPONENT_HIERARCHY_TYPES.COMPONENT) {
                if (node.parentId) {
                    changeInput = {
                        ...virtualComponentInputSample,
                        id: node.id,
                        field: 'replace-parent',
                        prevParentId: node.parentId,
                        newParentId: nextParentNode.id,
                    };
                } else {
                    changeInput = {
                        ...virtualComponentInputSample,
                        id: node.id,
                        field: 'add-parent',
                        newParentId: nextParentNode.id,
                    };
                }
            }
        } else if (node.parentId) {
            changeInput = {
                ...virtualComponentInputSample,
                id: node.id,
                field: 'remove-parent',
                prevParentId: node.parentId,
            };
        }
        return changeInput;
    }, []);

    const handleDragComponent = (treeData, node, nextParentNode, prevPath, prevTreeIndex, nextPath) => {
        const isSelectedDescendant = isItemSelectedDescendant(node, nextParentNode, flatTreeData);
        if (isSelectedDescendant) {
            setFlags([
                {
                    icon: <WarningIcon primaryColor={RED} />,
                    title: `Warning`,
                    description: `You are not allowed to drop ${node.name} into a descendant of it.`,
                    key: 'Warning',
                    autoDismiss: true,
                    actions: [
                        {
                            content: 'Dismiss',
                            onClick: () => {
                                setFlags([]);
                            },
                        },
                    ],
                },
            ]);
            return;
        }
        /**
         * First step: Check if need to change parent
         */
        const changesToBeApplied = [];
        const changeComponentParentId = getChangeComponentParentIdInput2(node, nextParentNode);
        if (changeComponentParentId) changesToBeApplied.push(changeComponentParentId);
        /**
         * Flat tree data to be easier to work with tree data
         */
        const flatData = getFlatDataFromTree({
            treeData,
            getNodeKey: ({ node }) => node.treeId,
            ignoreCollapsed: false,
        });
        /**
         * Get current component with all components in the current component subtree
         */
        const allComponentsChildrenInTheTree = getAllComponentChildrenFrom(node);
        /**
         * Get current component root item in the tree
         */
        const { node: currentComponentRootItem } = flatData.find((item) => item.node.treeId === prevPath[0]);
        /**
         * Get current component parent label
         */
        const componentParentLabel = getComponentParentLabel(prevPath, flatData);
        /**
         * Check changes in the current component and its children
         */
        allComponentsChildrenInTheTree.forEach((component) => {
            let newLabels = component.virtualLabels || [];
            /**
             * Check if the current component is present in the same subtree. This is necessary to avoid
             * removing unnecessary labels.
             * If has same component in other parts then remove only parent label
             * Otherwise, remove all labels.
             */
            const hasSameComponent = hasSameComponentRootSubTree(currentComponentRootItem, component);
            if (hasSameComponent) {
                newLabels = newLabels.filter((label) => label.id !== componentParentLabel.id);
            } else {
                newLabels = [];
            }

            /**
             * Check if is dropping in another component/label to update labels
             */
            if (nextParentNode) {
                const nextParentLabels = getParentLabelsPath(nextPath, flatData);
                newLabels = [...newLabels, ...nextParentLabels];
            }

            const componentLabels = (component.virtualLabels || []).map((label) => label.id);
            const finalLabels = newLabels.map((label) => label.id);

            /**
             * Check if new labels are exactly like the old ones
             */
            if (!isEmpty(xor(componentLabels, finalLabels))) {
                /**
                 * Adds input change to send to backend
                 */
                changesToBeApplied.push({
                    ...virtualComponentInputSample,
                    id: component.id,
                    field: 'labels',
                    virtualLabels: newLabels.map((label) => ({
                        id: label.id || 'new',
                        name: label.label,
                    })),
                    assigneeType: component.assigneeType,
                });
            }
        });
        updateMultipleComponents(changesToBeApplied);
    };

    const handleDragLabel = useCallback(() => {
        setFlags([
            {
                icon: <WarningIcon primaryColor={RED} />,
                title: `Action not allowed`,
                description: `It's not possible to drop a label into a label.`,
                key: 'Warning',
                autoDismiss: true,
                actions: [
                    {
                        content: 'Dismiss',
                        onClick: () => {
                            setFlags([]);
                        },
                    },
                ],
            },
        ]);
    }, []);

    // Executes after onTreeChange
    const onNodeMove = useCallback(
        ({ treeData, node, nextParentNode, prevPath, prevTreeIndex, nextPath, nextTreeIndex }) => {
            /* Add/Change parent or label */
            if (nextParentNode) {
                if (nextParentNode.type === COMPONENT_HIERARCHY_TYPES.COMPONENT) {
                    /* Drop label into component: does not allow */
                    if (node.type === COMPONENT_HIERARCHY_TYPES.LABEL) {
                        handleDragLabelDropIntoComponent();
                        return;
                    }
                    if (node.type === COMPONENT_HIERARCHY_TYPES.COMPONENT) {
                        /* Drop component into component: change component parent */
                        handleDragComponent(
                            treeData,
                            node,
                            nextParentNode,
                            prevPath,
                            prevTreeIndex,
                            nextPath,
                            nextTreeIndex,
                        );
                    }
                } else {
                    /* Drop component into label */
                    // eslint-disable-next-line no-lonely-if
                    if (node.type === COMPONENT_HIERARCHY_TYPES.COMPONENT) {
                        handleDragComponent(
                            treeData,
                            node,
                            nextParentNode,
                            prevPath,
                            prevTreeIndex,
                            nextPath,
                            nextTreeIndex,
                        );
                    } else if (node.type === COMPONENT_HIERARCHY_TYPES.LABEL) {
                        /* Drop label into label */
                        handleDragLabel();
                        return;
                    }
                }
            } else {
                /* Updating component removing parent and labels */
                // eslint-disable-next-line no-lonely-if
                if (node.type === COMPONENT_HIERARCHY_TYPES.COMPONENT) {
                    handleDragComponent(
                        treeData,
                        node,
                        nextParentNode,
                        prevPath,
                        prevTreeIndex,
                        nextPath,
                        nextTreeIndex,
                    );
                } else if (node.type === COMPONENT_HIERARCHY_TYPES.LABEL) {
                    /* Updating label removing parent */
                    handleDragLabel();
                    return;
                }
            }

            setTreeData(treeData);
        },
        [],
    );

    const canDrop = useCallback(
        ({ node, nextParent }) => {
            if (nextParent && node.id === nextParent.id) {
                return false;
            }
            const isDescendant = isItemSelectedDescendant(node, nextParent, flatTreeData);
            return !(nextParent && isDescendant);
        },
        [flatTreeData],
    );

    const canDrag = () => {
        return !!hasPermissionToEdit();
    };

    // Executes before onNodeMove
    const onTreeChange = useCallback((treeData) => {
        const newExpandedInfo = {};
        const flatData = getFlatDataFromTree({
            treeData,
            getNodeKey: ({ node }) => node.treeId,
            ignoreCollapsed: false,
        });
        flatData.forEach((flatItem) => {
            newExpandedInfo[flatItem.node.path.toString()] = flatItem.node.expanded;
        });
        setTreeDataExpandedNodes(newExpandedInfo);
    }, []);

    const onVisibilityToggle = useCallback(({ treeData }) => {
        setTreeData(treeData);
    }, []);

    const handleNodeClick = useCallback(
        (node) => {
            const vc = virtualComponents.find((vc) => vc.id === node.id);
            editVirtualComponent(vc, treeData);
        },
        [editVirtualComponent, virtualComponents, treeData],
    );

    useEffect(() => {
        const labels = (filters.labels || []).map(({ value }) => value);
        const projects = (filters.projects || []).map(({ value }) => value);
        const variables = {
            filter: {
                virtualComponentName: filters.search,
                labels,
                projects,
                shouldGroupByLabels,
            },
            pagination,
        };
        setIsLoading(true);
        client
            .query({
                query: VIRTUAL_COMPONENTS_HIERARCHY_QUERY_PAGINATED,
                variables,
                fetchPolicy: 'network-only',
            })
            .then(({ data }) => {
                const vcs = (data.virtualComponentsHierarchyPaginated?.components || []).map((vc) => {
                    const treeId = uuidV4();
                    return {
                        ...vc,
                        treeId,
                        path: [treeId],
                    };
                });
                setVirtualComponents(vcs);
                const lbls = (data.virtualComponentsHierarchyPaginated?.labels || []).map((label) => {
                    const labelTreeId = uuidV4();
                    return {
                        ...label,
                        treeId: labelTreeId,
                        path: [labelTreeId],
                    };
                });
                setLabels(lbls);
                setCount(data.virtualComponentsHierarchyPaginated?.count);
                setIsLoading(false);
                setIsFirstLoading(false);
            })
            .catch(() => {
                setIsLoading(false);
                setIsFirstLoading(false);
            });
    }, [client, pagination, filters.search, filters.labels, filters.projects, shouldGroupByLabels]);

    // to avoid have an arrow open when return to a page that a item was opened before.
    useEffect(() => setTreeDataExpandedNodes([]), [pagination, filters.projects, filters.labels]);

    const onExpandNode = useCallback(
        (node) => {
            const childrenCalled = treeChildrenDataCalled?.[node.path?.toString()];
            if (childrenCalled) {
                return;
            }
            setIsLoading(true);
            if (node.type === COMPONENT_HIERARCHY_TYPES.LABEL) {
                const variables = {
                    labelId: node.id,
                };
                client
                    .query({
                        query: VIRTUAL_LABELS_CHILDREN,
                        variables,
                        fetchPolicy: 'network-only',
                    })
                    .then(({ data }) => {
                        const children = data.virtualLabelsChildren?.components?.map((child) => {
                            const treeId = uuidV4();
                            return {
                                ...child,
                                treeId,
                                type: COMPONENT_HIERARCHY_TYPES.COMPONENT,
                                parentId: node.id,
                                parentTreeId: node.treeId,
                                path: [...node.path, treeId],
                            };
                        });
                        setVirtualComponents((current) => [...current, ...children]);
                        const childrenLbls = data.virtualLabelsChildren.labels?.map((label) => {
                            const treeId = uuidV4();
                            return {
                                ...label,
                                treeId,
                                type: COMPONENT_HIERARCHY_TYPES.LABEL,
                                parentId: node.id,
                                parentTreeId: node.treeId,
                                path: [...node.path, treeId],
                            };
                        });
                        setLabels((current) => [...current, ...childrenLbls]);
                        setIsLoading(false);
                        setTreeChildrenDataCalled((current) => ({
                            ...current,
                            [node.path?.toString()]: true,
                        }));
                    })
                    .catch(() => {
                        setIsLoading(false);
                    });
            } else {
                const variables = {
                    componentId: node.id,
                };
                client
                    .query({
                        query: VIRTUAL_COMPONENTS_HIERARCHY_CHILDREN,
                        variables,
                        fetchPolicy: 'network-only',
                    })
                    .then(({ data }) => {
                        const children = data.virtualComponentsHierarchyChildren?.map((child) => {
                            const treeId = uuidV4();
                            return {
                                ...child,
                                treeId,
                                type: COMPONENT_HIERARCHY_TYPES.COMPONENT,
                                parentId: node.id,
                                parentTreeId: node.treeId,
                                path: [...node.path, treeId],
                            };
                        });
                        setVirtualComponents((current) => [...current, ...children]);
                        setIsLoading(false);
                        setTreeChildrenDataCalled((current) => ({
                            ...current,
                            [node.path?.toString()]: true,
                        }));
                    })
                    .catch(() => {
                        setIsLoading(false);
                    });
            }
        },
        [client, treeChildrenDataCalled],
    );

    if (isLoading && isFirstLoading) {
        return (
            <LoadingContainer>
                <Loader size="large" withFullWidth={true} />
            </LoadingContainer>
        );
    }

    return (
        <>
            <div style={{ minWidth: '700px' }}>
                <GridWrapper
                    style={{
                        width: '100%',
                        paddingTop: '16px',
                        paddingBottom: '8px',
                        borderBottom: `1px solid ${token('color.border')}`,
                        fontWeight: 'bold',
                    }}
                >
                    <div style={{ gridColumn: 'first-col' }}>
                        <TableHeader>Component</TableHeader>
                    </div>
                    <div>
                        <TableHeader>Projects</TableHeader>
                    </div>
                    <div>
                        <TableHeader>Component lead</TableHeader>
                    </div>
                    <div>
                        <TableHeader>Default assignee</TableHeader>
                    </div>
                    <div>
                        <TableHeader>Issues</TableHeader>
                    </div>
                </GridWrapper>
                {!hasFilters() && treeData.length === 0 && <EmptyListView />}
                {hasFilters() && treeData.length === 0 && <EmptyFilterView />}
                <LoadingOverlay
                    active={isLoading}
                    spinner={<Spinner />}
                    styles={{
                        overlay: (base) => ({
                            ...base,
                            background: colorMode === 'dark' ? 'rgba(35, 35, 40, 0.5)' : 'rgba(235, 236, 240, 0.3)',
                        }),
                    }}
                >
                    <SortableTree
                        treeData={treeData}
                        onChange={onTreeChange}
                        onMoveNode={onNodeMove}
                        onVisibilityToggle={onVisibilityToggle}
                        isVirtualized={false}
                        theme={FileExplorerTheme}
                        getNodeKey={({ node }) => node.treeId}
                        rowHeight={42}
                        scaffoldBlockPxWidth={20}
                        canDrag={canDrag}
                        canDrop={canDrop}
                        generateNodeProps={({ node }) => {
                            return {
                                onClickName: () => {
                                    handleNodeClick(node);
                                },
                                onClickDeleteLabel: () => {
                                    setToDeleteLabel(node);
                                },
                                onExpandNode: () => {
                                    onExpandNode(node);
                                },
                            };
                        }}
                        onDragStateChanged={noop}
                    />
                </LoadingOverlay>
                <PaginationContainer
                    rowsCount={count}
                    initialPageSize={pagination.pageSize}
                    onPageChange={onPageChange}
                />
            </div>

            <FlagGroup>
                {flags.map((flag) => (
                    <Flag key={flag.key} {...flag} />
                ))}
            </FlagGroup>
        </>
    );
};

export default VirtualComponentsHierarchyTable;
